'use client';
import { Button } from '@components/Button';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { FC, Fragment } from 'react';
import { BreadcrumbItem, BreadcrumbsNav } from './styles';

interface BreadcrumbProps {
  shopname?: string;
  isShopPage?: boolean;
  marginBottom?: string;
}

const Breadcrumbs: FC<BreadcrumbProps> = ({ shopname, isShopPage, marginBottom }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const localePaths = ['en', 'fr-CA', 'fr-ca', 'es', 'en-US'];
  const pathsExcludedFromBreadcrumbs = [...localePaths, 'shops', shopname];
  const pathnames = pathname.split('/').filter((x) => x);
  const invalidPathnames = [
    'explore',
    'company',
    'lifestyle-properties',
    'featured-areas',
    'lifestyle',
    'dashboard',
    'resources',
    'ranch-dev',
  ];
  const searchParams = useSearchParams();
  const listingTypes = searchParams.get('listingTypes');
  const oneListingType = listingTypes === 'For Sale' || listingTypes === 'For Rent';
  const localeInPathname = localePaths.some((locale) => pathname.includes(`/${locale}`));

  let updatedPathnames = pathnames;

  const decodeAndFormatBreadcrumb = (str: string) => {
    const decodedString = decodeURIComponent(str);

    const stringWithSpaces = decodedString.replace(/-/g, ' ').split(' ');
    return stringWithSpaces
      .map((part) =>
        part.toLowerCase() !== 'and' ? part.charAt(0).toUpperCase() + part.slice(1) : part,
      )
      .join(' ');
  };

  // Adds correct listing types to breadcrumbs
  if (oneListingType) {
    updatedPathnames = [...pathnames, listingTypes];
  }

  return (
    (updatedPathnames?.length > 1 ||
      (isShopPage && !localeInPathname && updatedPathnames.length > 0)) && (
      <BreadcrumbsNav style={{ marginBottom: marginBottom }}>
        <ul style={{ display: 'flex' }}>
          <BreadcrumbItem data-testid="home-breadcrumb">
            <Link
              style={{ color: '#666666', fontSize: '14px' }}
              href="/"
              data-testid="home-breadcrumb-link"
            >
              {t('Home')}
            </Link>
          </BreadcrumbItem>
          <Button
            variant="iconOnly"
            arrow={false}
            icon="ArrowRight"
            label="Right Arrow"
            iconSize="20px"
            disabled
          />

          {updatedPathnames.map((name, index) => {
            const path = `/${updatedPathnames.slice(0, index + 1).join('/')}`;
            const nameNotExcluded = pathsExcludedFromBreadcrumbs.every((path) => {
              return path !== name;
            });

            let updatedName = name;
            let updatedPath = path;

            // different index used in path checks when shopname and locale are included in path
            const indexUsed = (index: number) => {
              if (shopname && path.includes(shopname)) {
                index++;

                if (localeInPathname) {
                  index++;
                }

                return index;
              } else {
                return index;
              }
            };

            // Changes path used for advisor locator breadcrumb if not shop page
            if (pathname.includes('our-advisors') && index === indexUsed(1) && !isShopPage) {
              updatedPath = '/shops-and-advisors/advisor-locator?currentPage=1';
            }

            const indexForListingType = pathname.includes('/our-listings')
              ? index === indexUsed(3)
              : index === indexUsed(2);

            const indexForListingTypeShopPage =
              isShopPage && (pathname.includes('/our-listings') ? index === 2 : index === 1);

            if (
              pathname.includes('/properties') &&
              // Shop pages without locale and shopname in pathname will use a different index
              (indexForListingType || (indexForListingTypeShopPage && !localeInPathname)) &&
              oneListingType
            ) {
              // Get correct path if one listing type is in search params
              if (listingTypes === 'For Sale') {
                updatedPath = `${pathname}?listingTypes=For+Sale`;
              }

              if (listingTypes === 'For Rent') {
                updatedPath = `${pathname}?listingTypes=For+Rent`;
              }
            }

            const disableInvalidPaths = invalidPathnames.some(
              (invalidPath) => name === invalidPath,
            );
            const disableAdvisorName = pathname.includes('our-advisors') && index === indexUsed(2);

            const advisorIdShown = pathname.includes('our-advisors') && index === indexUsed(3);
            const propertyDetailsShown =
              pathname.includes('our-listings') &&
              !oneListingType &&
              (isShopPage && !localeInPathname ? index === 2 : index === 3);
            const developmentListingShown =
              pathname.includes('development-services') && index === 4;
            const blogShown =
              pathname.includes('blog') &&
              (isShopPage && !localeInPathname ? index === 1 : index === 2);
            const pressReleaseShown = pathname.includes('press-releases') && index === 4;

            // Update names used for breadcrumbs
            if (advisorIdShown) {
              updatedName = 'Advisor';
            }

            if (blogShown || pressReleaseShown) {
              updatedName = 'Article';
            }

            if (propertyDetailsShown || developmentListingShown) {
              updatedName = 'Property Details';
            }

            const lastItem = index === updatedPathnames.length - 1;

            if ((index > 0 || isShopPage) && nameNotExcluded) {
              return (
                <Fragment key={path}>
                  <BreadcrumbItem data-testid={`${updatedName}-breadcrumb`}>
                    <Link
                      style={{
                        fontSize: '14px',
                        color:
                          disableAdvisorName || disableInvalidPaths
                            ? '#808080'
                            : lastItem
                            ? 'black'
                            : '#666666',
                        pointerEvents: disableAdvisorName || disableInvalidPaths ? 'none' : 'auto',
                      }}
                      href={updatedPath}
                      data-testid={`${updatedName}-breadcrumb-link`}
                    >
                      {t(decodeAndFormatBreadcrumb(updatedName) as any)}
                    </Link>
                  </BreadcrumbItem>
                  {index < updatedPathnames.length - 1 && (
                    <Button
                      variant="iconOnly"
                      arrow={false}
                      icon="ArrowRight"
                      label="Right Arrow"
                      iconSize="20px"
                      disabled
                    />
                  )}
                </Fragment>
              );
            }
          })}
        </ul>
      </BreadcrumbsNav>
    )
  );
};

export default Breadcrumbs;
